import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  Bug,
  Lightbulb,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  Eye,
  MessageCircle,
  User,
} from 'lucide-react';
import { getSupabaseAdminClient, Feedback } from '@parentpath/supabase-client';
import toast from 'react-hot-toast';

const supabase = getSupabaseAdminClient();

type FeedbackType = 'all' | 'bug' | 'feature' | 'general' | 'complaint';
type FeedbackStatus = 'all' | 'open' | 'in_progress' | 'resolved' | 'closed';

interface FeedbackWithUser extends Feedback {
  user?: {
    email: string;
    display_name: string | null;
  } | null;
}

export function FeedbackPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<FeedbackType>('all');
  const [statusFilter, setStatusFilter] = useState<FeedbackStatus>('open');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackWithUser | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const pageSize = 10;
  const queryClient = useQueryClient();

  // Fetch feedback with pagination
  const { data: feedbackData, isLoading } = useQuery({
    queryKey: ['feedback', currentPage, searchQuery, typeFilter, statusFilter],
    queryFn: async () => {
      let query = supabase
        .from('feedback')
        .select(`
          *,
          user:profiles!feedback_user_id_fkey(email, display_name)
        `, { count: 'exact' });

      // Apply search filter
      if (searchQuery) {
        query = query.or(`subject.ilike.%${searchQuery}%,message.ilike.%${searchQuery}%`);
      }

      // Apply type filter
      if (typeFilter !== 'all') {
        query = query.eq('type', typeFilter);
      }

      // Apply status filter
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      // Apply pagination
      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;

      const { data, count, error } = await query
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;

      return {
        items: data as FeedbackWithUser[],
        totalCount: count || 0,
      };
    },
  });

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['feedback-stats'],
    queryFn: async () => {
      const [openResult, inProgressResult, resolvedResult, bugsResult] = await Promise.all([
        supabase.from('feedback').select('id', { count: 'exact', head: true }).eq('status', 'open'),
        supabase.from('feedback').select('id', { count: 'exact', head: true }).eq('status', 'in_progress'),
        supabase.from('feedback').select('id', { count: 'exact', head: true }).eq('status', 'resolved'),
        supabase.from('feedback').select('id', { count: 'exact', head: true }).eq('type', 'bug').eq('status', 'open'),
      ]);

      return {
        open: openResult.count || 0,
        inProgress: inProgressResult.count || 0,
        resolved: resolvedResult.count || 0,
        openBugs: bugsResult.count || 0,
      };
    },
  });

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status, notes }: { id: string; status: string; notes?: string }) => {
      const updates: Record<string, unknown> = { status };
      if (notes !== undefined) {
        updates.admin_notes = notes;
      }
      if (status === 'resolved') {
        updates.resolved_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('feedback')
        .update(updates as never)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
      queryClient.invalidateQueries({ queryKey: ['feedback-stats'] });
      toast.success('Status aktualisiert');
      setSelectedFeedback(null);
    },
    onError: () => {
      toast.error('Fehler beim Aktualisieren');
    },
  });

  const totalPages = Math.ceil((feedbackData?.totalCount || 0) / pageSize);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'bug':
        return <Bug className="w-4 h-4 text-red-500" />;
      case 'feature':
        return <Lightbulb className="w-4 h-4 text-amber-500" />;
      case 'complaint':
        return <AlertCircle className="w-4 h-4 text-orange-500" />;
      default:
        return <MessageSquare className="w-4 h-4 text-blue-500" />;
    }
  };

  const getTypeBadge = (type: string) => {
    const labels: Record<string, { label: string; bg: string; text: string }> = {
      bug: { label: 'Bug', bg: 'bg-red-100', text: 'text-red-700' },
      feature: { label: 'Feature', bg: 'bg-amber-100', text: 'text-amber-700' },
      complaint: { label: 'Beschwerde', bg: 'bg-orange-100', text: 'text-orange-700' },
      general: { label: 'Allgemein', bg: 'bg-blue-100', text: 'text-blue-700' },
    };
    const config = labels[type] || labels.general;
    return (
      <span className={`px-2 py-1 ${config.bg} ${config.text} text-xs rounded-full`}>
        {config.label}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'open':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
            <MessageCircle className="w-3 h-3" />
            Offen
          </span>
        );
      case 'in_progress':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">
            <Clock className="w-3 h-3" />
            In Bearbeitung
          </span>
        );
      case 'resolved':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
            <CheckCircle className="w-3 h-3" />
            Gelöst
          </span>
        );
      case 'closed':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded-full">
            <XCircle className="w-3 h-3" />
            Geschlossen
          </span>
        );
      default:
        return null;
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">User-Feedback</h1>
        <p className="text-slate-600">Feedback und Anfragen verwalten</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">{stats?.open || 0}</div>
              <div className="text-sm text-slate-600">Offen</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">{stats?.inProgress || 0}</div>
              <div className="text-sm text-slate-600">In Bearbeitung</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">{stats?.resolved || 0}</div>
              <div className="text-sm text-slate-600">Gelöst</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Bug className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">{stats?.openBugs || 0}</div>
              <div className="text-sm text-slate-600">Offene Bugs</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <form onSubmit={handleSearch} className="flex-1 min-w-[300px]">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Nach Betreff oder Nachricht suchen..."
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
              />
            </div>
          </form>
          <div className="flex gap-2">
            <select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value as FeedbackType);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
            >
              <option value="all">Alle Typen</option>
              <option value="bug">Bug</option>
              <option value="feature">Feature</option>
              <option value="general">Allgemein</option>
              <option value="complaint">Beschwerde</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as FeedbackStatus);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
            >
              <option value="all">Alle Status</option>
              <option value="open">Offen</option>
              <option value="in_progress">In Bearbeitung</option>
              <option value="resolved">Gelöst</option>
              <option value="closed">Geschlossen</option>
            </select>
          </div>
        </div>
      </div>

      {/* Feedback Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Typ</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Betreff</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Von</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Datum</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    Lädt...
                  </td>
                </tr>
              ) : feedbackData?.items?.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    Kein Feedback gefunden
                  </td>
                </tr>
              ) : (
                feedbackData?.items?.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {getTypeIcon(item.type)}
                        {getTypeBadge(item.type)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <span className="text-sm font-medium text-slate-900">{item.subject}</span>
                        <div className="text-xs text-slate-500 truncate max-w-xs">{item.message}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <User className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-600">
                          {item.user?.display_name || item.user?.email || 'Anonym'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-600">{formatDate(item.created_at)}</span>
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(item.status)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            setSelectedFeedback(item);
                            setAdminNotes(item.admin_notes || '');
                          }}
                          className="p-1 hover:bg-slate-100 rounded"
                          title="Details & Bearbeiten"
                        >
                          <Eye className="w-4 h-4 text-slate-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
          <div className="text-sm text-slate-600">
            Zeige {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, feedbackData?.totalCount || 0)} von {feedbackData?.totalCount?.toLocaleString('de-DE') || 0}
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {Array.from({ length: Math.min(5, totalPages || 1) }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-1 border border-slate-300 rounded ${
                  currentPage === page
                    ? 'bg-teal-500 text-white border-teal-500'
                    : 'hover:bg-slate-100'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages || 1, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedFeedback && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {getTypeIcon(selectedFeedback.type)}
                  <h2 className="text-xl font-semibold text-slate-900">{selectedFeedback.subject}</h2>
                </div>
                <button
                  onClick={() => setSelectedFeedback(null)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-4 text-sm text-slate-500">
                <div className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {selectedFeedback.user?.display_name || selectedFeedback.user?.email || 'Anonym'}
                </div>
                <div>{formatDate(selectedFeedback.created_at)}</div>
                {getTypeBadge(selectedFeedback.type)}
                {getStatusBadge(selectedFeedback.status)}
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-slate-700 mb-2">Nachricht</h3>
                <p className="text-slate-600 whitespace-pre-wrap">{selectedFeedback.message}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-slate-700 mb-2">Admin-Notizen</h3>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                  rows={3}
                  placeholder="Interne Notizen hinzufügen..."
                />
              </div>
              <div className="flex gap-2 pt-4">
                <button
                  onClick={() => updateStatusMutation.mutate({
                    id: selectedFeedback.id,
                    status: 'in_progress',
                    notes: adminNotes,
                  })}
                  disabled={selectedFeedback.status === 'in_progress'}
                  className="px-4 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 disabled:opacity-50"
                >
                  In Bearbeitung
                </button>
                <button
                  onClick={() => updateStatusMutation.mutate({
                    id: selectedFeedback.id,
                    status: 'resolved',
                    notes: adminNotes,
                  })}
                  disabled={selectedFeedback.status === 'resolved'}
                  className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 disabled:opacity-50"
                >
                  Als gelöst markieren
                </button>
                <button
                  onClick={() => updateStatusMutation.mutate({
                    id: selectedFeedback.id,
                    status: 'closed',
                    notes: adminNotes,
                  })}
                  disabled={selectedFeedback.status === 'closed'}
                  className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 disabled:opacity-50"
                >
                  Schließen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
